import React, { useCallback, useMemo } from 'react';
import Kanban from '../DesignSystems/Kanban';
import type {
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
} from '../DesignSystems/Kanban';
import Loading from '../Loading/Loading';
import { IapScope, INCIDENT } from './ICSConstants';
import { useIncidentActions } from './hooks/incidentActionHooks';
import { useOrgActions } from './hooks/orgActionHooks';
import { useActionFieldEdit } from './hooks/useActionFieldEdit';
import { useActionAssigneeDirectory } from './hooks/useActionAssigneeDirectory';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';
import { getActionCoordinates } from './utils/actionGeolocation';
import {
  groupActions,
  groupDropPatch,
  groupingIsDraggable,
  sortActionsForBoard,
  summarizeGroups,
  type ActionGrouping,
} from './actionsTable/actionGrouping';
import { ActionKanbanCard } from './actionsTable/ActionKanbanCard';
import type { ActionEntity } from './hooks/useScopedActionForm';

type ActionsKanbanProps = {
  scope?: IapScope;
  incidentIdOverride?: string | null;
  grouping?: ActionGrouping;
  onEditAction: (action: ActionEntity) => void;
  canEdit?: boolean;
  filteredActions?: ActionEntity[];
};

/**
 * The same tasks as the table, piled by status, assignee, priority or due
 * window. Drag only writes when the pile is a single field; dropping onto a
 * due-window column would have to invent a date, so those piles stay still.
 */
const ActionsKanban: React.FC<ActionsKanbanProps> = ({
  scope = INCIDENT,
  incidentIdOverride,
  grouping = 'status',
  onEditAction,
  canEdit = true,
  filteredActions,
}) => {
  const isIncident = scope === INCIDENT;
  const incidentQuery = useIncidentActions(scope, incidentIdOverride);
  const orgQuery = useOrgActions(scope);
  const { data: actions, isLoading } = isIncident ? incidentQuery : orgQuery;
  const { editAction } = useActionFieldEdit(scope, incidentIdOverride);
  const directory = useActionAssigneeDirectory(scope, incidentIdOverride);
  const zoomToLocation = useOptionalUscgMapContext()?.zoomToLocation;

  const activeActions = useMemo(
    () =>
      filteredActions ?? (actions ?? []).filter((action) => !action.archived),
    [actions, filteredActions]
  );

  const groups = useMemo(
    () =>
      groupActions(activeActions, grouping, { directory }).map((group) => ({
        ...group,
        actions: sortActionsForBoard(group.actions),
      })),
    [activeActions, grouping, directory]
  );

  const board: KanbanBoard = useMemo(
    () => ({
      columns: groups.map((group) => ({
        id: group.id,
        title: `${group.title} (${group.actions.length})`,
        cards: group.actions.map((action) => ({
          id: String(action.id),
          title: action.name,
          description: action.description ?? undefined,
          metadata: action,
        })),
      })),
    }),
    [groups]
  );

  const handleCardMove = useCallback(
    async (
      card: KanbanCard,
      _source: KanbanColumn,
      destination: KanbanColumn
    ) => {
      const action = card.metadata as ActionEntity | undefined;
      const patch = groupDropPatch(grouping, destination.id);
      if (!action || !patch || !canEdit) return;
      await editAction(action, patch);
    },
    [canEdit, editAction, grouping]
  );

  const renderCard = useCallback(
    (card: KanbanCard) => {
      const action = card.metadata as ActionEntity | undefined;
      if (!action) return null;
      return (
        <ActionKanbanCard
          action={action}
          directory={directory}
          onZoomToMap={(item) => {
            const coords = getActionCoordinates(
              (item as { geolocation?: unknown }).geolocation
            );
            if (coords && zoomToLocation) void zoomToLocation(coords);
          }}
        />
      );
    },
    [directory, zoomToLocation]
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div>
      <p className="tw:mb-3 tw:text-xs tw:text-foreground">
        {summarizeGroups(groups)}
        {groupingIsDraggable(grouping)
          ? ' · drag a card to change its group'
          : ' · this grouping is read-only'}
      </p>
      <div data-tour="hwcg-tasks-board">
        <Kanban
          board={board}
          onCardMove={
            canEdit && groupingIsDraggable(grouping)
              ? handleCardMove
              : undefined
          }
          onCardClick={(card) => {
            if (canEdit && card.metadata)
              onEditAction(card.metadata as ActionEntity);
          }}
          renderCard={renderCard}
          disableCardDrag={!canEdit || !groupingIsDraggable(grouping)}
        />
      </div>
    </div>
  );
};

export default ActionsKanban;
